import React, { PropTypes } from 'react';
import { utcMonday, timeYear, timeFormat, select } from "d3";

const dayTitles = ["M", "T", "W", "T", "F", "S", "S"];

const uiFormatDate = timeFormat('%A, %d-%m-%Y');
const formatDate = timeFormat('%Y-%m-%d');

const cellWidth = 25;
const cellHeight = cellWidth * 3;
const width = 1450;
const height = cellHeight * 7.5;

const locale = "en-gb"; // TO-DO: get from browser. Used in getting month name in monthInfo()


const getWeekNumber = (periodInfo, date) => {
  if (periodInfo.isFirstYear(date)) {
    return utcMonday.count(timeYear(periodInfo.start), date) - periodInfo.startWeekNumber;
  } else {
    return 53 - periodInfo.startWeekNumber + utcMonday.count(timeYear(periodInfo.end), date);
  }
};

const getDayNumber = (date) => {
  return (date.getDay() + 6) % 7; // day number - starting from Monday
};


const getDay = (date) => {
  return date.getDate()
};


const monthPath = (periodInfo, dateFrom) => {
  let d0, d1, t1, w0, w1;
  t1 = new Date(dateFrom.getFullYear(), dateFrom.getMonth() + 1, 0);
  d0 = getDayNumber(dateFrom);
  w0 = getWeekNumber(periodInfo, dateFrom);
  d1 = getDayNumber(t1);
  w1 = getWeekNumber(periodInfo, t1);
  return 'M' + (w0 + 1) * cellWidth + ',' + d0 * cellHeight + 'H' + w0 * cellWidth + 'V' + 7 * cellHeight + 'H' + w1 * cellWidth + 'V' + (d1 + 1) * cellHeight + 'H' + (w1 + 1) * cellWidth + 'V' + 0 + 'H' + (w0 + 1) * cellWidth + 'Z';
};


const monthInfo = (periodInfo, dateFrom) => {
	
	let startWeek = getWeekNumber(periodInfo, dateFrom);
	let lastDayOfMonth = new Date(dateFrom.getFullYear(), dateFrom.getMonth() + 1, 0);
	let endWeek = getWeekNumber(periodInfo, lastDayOfMonth);
	return {
		posX: (cellWidth + startWeek * cellWidth + ((endWeek - startWeek) * cellWidth) / 2.5),
		title: dateFrom.toLocaleString(locale, { month: "short" })
	}

}

const today = formatDate(new Date);


const getDayClasses = (date, formattedDate, dayNumber, bankHolidays, nonWorkingDates) => {
	let res = [];

	if (dayNumber === 5 || dayNumber === 6) {
		 res.push("weekend");
	}

	if (bankHolidays.includes(formattedDate)) {
		res.push("bank-holiday");
	}

	if (nonWorkingDates.includes(date)) {
		res.push("non-working");
	}

	if (today === formattedDate) {
		res.push("today");	
	}

	return res.join(" ");
};


const getFillersForDate = (date, fillerDateRecs, next) => {
	let array = fillerDateRecs[date];
	let reactBits = [];
	if (array !== undefined) {
		array.forEach( (filler, index) => {
			 filler.y = filler.y + 18 + index * 18;
			reactBits.push(next(filler));
		});
	}
	return reactBits;
};


const prepFillerDatesRecs = (fillers, datesWithFillers) => {
	const fillerDateRecs = {}; // { date: [fillers]}
	let dateRectangle, x, y;
	let filler, fillerLetter, fillerClass;

	datesWithFillers.forEach( (fillerIds, date) => {

		dateRectangle = document.getElementById("D" + date);
		x = parseInt(dateRectangle.getAttribute("x"));
		y = parseInt(dateRectangle.getAttribute("y"));

		fillerIds.forEach( (fillerId, index) => {
			filler = fillers.get(fillerId);

			fillerLetter = "X";
			if (filler.name !== undefined) {
	  		fillerLetter = filler.name[0];
	  	};
	  	fillerClass = "filler" + fillerId;

	  	if (!fillerDateRecs[date]) {
	  		fillerDateRecs[date] = [];
	  	}

			fillerDateRecs[date].push({
				x,
				y,
				class: fillerClass,
				letter: fillerLetter,
				name: filler.name,
				id: fillerId,
				key: index
			});

		});

	});

	return fillerDateRecs;
}




export const Calendar = ({ periodInfo, attachFiller, detachFiller, datesWithFillers, fillers, specialDays }) => {
	
	if (periodInfo.size === 0) {
		return null;
	}

  const clickDate = (formattedDate) => {
		attachFiller(formattedDate)
	};


	let clickFiller = (formattedDate, fillerDateRec) => {
  	detachFiller({ date: formattedDate, fillerId: fillerDateRec.id, index: fillerDateRec.key })
  };



  let bankHolidays = [];
  let bh = specialDays.get("bankHolidayDates");
  if (bh && bh.size > 0)
  {
  	bankHolidays = bh;
  }

  
  let nonWorkingDates = [];
  let nw = specialDays.get("nonWorkingDates");
	if (nw && nw.size > 0)
  {
  	nonWorkingDates = nw;
  }  


  let fillerDateRecs = prepFillerDatesRecs(fillers, datesWithFillers);

  

  return (
		<svg width={width} height={height}>
			<g transform={"translate(" + ((width - cellWidth * 53) / 2) + "," + (height - (cellHeight * 7) - 1) + ")"} >
				<text 
					transform={"translate(-35," + cellHeight * 3.5 + ")rotate(-90)"}
					className="yearsLabel">{periodInfo.yearLabel}</text>
				{dayTitles.map( (dayTitle, index) =>
					<text
						key={dayTitle + index}
						x="-15"
						y={(cellHeight / 1.7) + cellHeight * index}
						className="dayLabel">{dayTitle}</text>
				)}
				{periodInfo.days.map( (date) => {
					let formattedDate = formatDate(date);
					let uiFormattedDate = uiFormatDate(date);
					let dayNumber = getDayNumber(date);
					let weekNumber = getWeekNumber(periodInfo, date);
					return <g key={"d-g" + formattedDate}>
						<rect
							onClick={() => clickDate(formattedDate)}
							id={"D" + formattedDate}
							className={"day " + getDayClasses(date, formattedDate, dayNumber, bankHolidays, nonWorkingDates)}
							width={cellWidth}
							height={cellHeight}
							x={weekNumber * cellWidth}
							y={dayNumber * cellHeight}>
						</rect>
						{getFillersForDate(formattedDate, fillerDateRecs, (fillerDateRec) =>
							<g key={"f-g" + fillerDateRec.key}>
								<text 
									x={fillerDateRec.x + (cellWidth / 4)}
									y={fillerDateRec.y + 13}
									className="filler">
									{fillerDateRec.letter}
								</text>
								<rect x={fillerDateRec.x} 
											y={fillerDateRec.y}
											height="18"
											width={cellWidth}
											onClick={() => clickFiller(formattedDate, fillerDateRec)}
											className={fillerDateRec.class}>
									<title>{fillerDateRec.name}</title>				
								</rect>
							</g>
						)}
						<text 
							className="dayNumber"
							x={weekNumber * cellWidth + (cellWidth / 4)}
							y={dayNumber * cellHeight + 10}
							>{getDay(date)}</text>
						<title>{uiFormattedDate}</title>
					</g>
				})}
				{periodInfo.months.map( (month) =>
					<path key={month} className="month" d={monthPath(periodInfo, month)}></path>
				)}
				{periodInfo.months.map( (month) => {
					let monthTitleInfo = monthInfo(periodInfo, month);
					return <text
						key={"m" + month}
						y="-10"
						x={monthTitleInfo.posX}
						className="monthLabel">{monthTitleInfo.title}</text>
				})}
			</g>
		</svg>
	)

}


/** WEBPACK FOOTER **
 ** ./web/static/js/react/presentationals/calendar.js
 **/